import {
    Box,
    GridLegacy as Grid,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableSortLabel
} from '@mui/material';

import {
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TableFooter,
    TableFooterCell
} from '@/components/shared/styles/Table';
import { Heading, SMBold } from '@/typography/Typography';
import { Dropdown } from '@/components/shared/Dropdown';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useFormatting } from '@/utils/formatting';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { SearchGrid } from '@/components/SearchGrid';
import Chip from '@/components/shared/Chip';
import {
    useReportsQuery,
    useSicknessReportPdfDownload
} from '@/services/reportsQuery';
import { useErrorHandling } from '@/utils/errorHandling';
import { TableCard } from '../shared/Card';
import {
    PrimaryButton,
    SecondaryButtonRegular
} from '@/components/shared/Button';
import { downloadExcel } from '@/utils/excelExport';

type SortingType = 'asc' | 'desc' | undefined;

/**
 * Month abbreviations as the reports service emits them. It builds them with
 * `CultureInfo.CurrentCulture` (`Reports.Internal/ReportFunctions.cs`), so a host configured for a
 * non-English culture would send names this map does not carry.
 */
const MONTH_NUMBER_BY_ABBREVIATION: Record<string, number> = {
    jan: 1,
    feb: 2,
    mar: 3,
    apr: 4,
    may: 5,
    jun: 6,
    jul: 7,
    aug: 8,
    sep: 9,
    oct: 10,
    nov: 11,
    dec: 12
};

/**
 * The first of a sick-day's month as a date-only ISO string, which `downloadExcel` converts into a
 * real Date so Excel offers its year > month filter tree over the column.
 *
 * Falls back to the plain "Sep 2026" label when the month name is not recognised: an unfilterable
 * text cell is recoverable, a confidently wrong date is not.
 */
export const sickDayMonthCell = (sickDay: {
    monthName?: string;
    year?: number;
}) => {
    const month =
        MONTH_NUMBER_BY_ABBREVIATION[
            sickDay?.monthName?.slice(0, 3).toLowerCase() ?? ''
        ];

    return month && sickDay?.year
        ? `${sickDay.year}-${String(month).padStart(2, '0')}-01`
        : `${sickDay?.monthName ?? ''} ${sickDay?.year ?? ''}`.trim();
};

export const SicknessReport = () => {
    const dispatch = useAppDispatch();

    const [departments, setDepartments] = useState<string[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<string>();

    const [employeesList, setEmployeesList] = useState<any[]>([]);
    const [sortedEmployees, setSortedEmployees] = useState<any[]>([]);

    const { setAlert: _setAlert } = useNotificationsSnackbar();

    const { toDecimal } = useFormatting();

    const { t } = useTranslation();

    const { getEmployeeSicknessQuery, getDepartmentsQuery } = useReportsQuery({
        enableGetEmployeeDetailsQuery: false,
        enableGetDepartmentsQuery: true,
        enableGetEmployeeSicknessQuery: departments?.length > 0,
        employeeDetailParams: {
            departments:
                selectedDepartment == 'All' ? undefined : selectedDepartment
        }
    });

    useEffect(() => {
        if (getEmployeeSicknessQuery.data) {
            setEmployeesList(getEmployeeSicknessQuery.data.data.employees);
        }
    }, [getEmployeeSicknessQuery.data]);

    useEffect(() => {
        if (getDepartmentsQuery.data) {
            setDepartments([
                'All',
                ...getDepartmentsQuery.data.data.plandayDepartments.map(
                    (d: any) => d.name
                )
            ]);
            if (!selectedDepartment) {
                setSelectedDepartment('All');
            }
        }
    }, [getDepartmentsQuery.data]);

    useLayoutEffect(() => {
        dispatch(
            setLoader(
                getEmployeeSicknessQuery.isPending ||
                    getDepartmentsQuery.isPending
            )
        );
    }, [getEmployeeSicknessQuery.isPending, getDepartmentsQuery.isPending]);

    const { backendErrorHandler } = useErrorHandling();

    const downloadPdf = useSicknessReportPdfDownload();

    useEffect(() => {
        if (getEmployeeSicknessQuery.error) {
            backendErrorHandler()(getEmployeeSicknessQuery.error);
        }
    }, [getEmployeeSicknessQuery.error]);

    useEffect(() => {
        if (downloadPdf.error) {
            backendErrorHandler()(downloadPdf.error);
        }
    }, [downloadPdf.error]);

    useEffect(() => {
        if (getDepartmentsQuery.error) {
            backendErrorHandler()(getDepartmentsQuery.error);
        }
    }, [getDepartmentsQuery.error]);

    const [nameSortDirection, setNameSortDirection] =
        useState<SortingType>(undefined);
    const [sumSortDirection, setSumSortDirection] =
        useState<SortingType>('desc');

    useEffect(() => {
        let sortedEmployees = [...employeesList];

        if (nameSortDirection === 'desc') {
            sortedEmployees = sortedEmployees.sort((a, b) => {
                return b?.employeeName?.localeCompare(a.employeeName);
            });
        }
        if (nameSortDirection === 'asc') {
            sortedEmployees = sortedEmployees.sort((a, b) => {
                return a?.employeeName?.localeCompare(b.employeeName);
            });
        }

        if (sumSortDirection === 'asc') {
            sortedEmployees = sortedEmployees.sort((a, b) => {
                return a?.sum - b.sum;
            });
        }
        if (sumSortDirection === 'desc') {
            sortedEmployees = sortedEmployees.sort((a, b) => {
                return b.sum - a?.sum;
            });
        }

        setSortedEmployees(sortedEmployees);
    }, [employeesList, sumSortDirection, nameSortDirection]);

    // Calculate monthly totals
    const calculateMonthlyTotals = () => {
        if (!employeesList.length) return [];

        const monthlyTotals: { [key: string]: number } = {};

        employeesList.forEach((employee) => {
            employee.sickDays?.forEach((sickDay: any) => {
                const key = `${sickDay.monthName}_${sickDay.year}`;
                monthlyTotals[key] =
                    (monthlyTotals[key] || 0) + parseFloat(sickDay.hours || 0);
            });
        });

        return (
            employeesList[0]?.sickDays?.map((sickDay: any) => {
                const key = `${sickDay.monthName}_${sickDay.year}`;
                return monthlyTotals[key] || 0;
            }) || []
        );
    };

    const handleDownloadExcel = () => {
        // One row per employee-month rather than the screen's month-per-column matrix. Excel's
        // hierarchical year > month > day filter and its pivot tables both work on column values,
        // so a month has to BE a value to be filterable — as a column header it is unreachable.
        // The wide, screen-shaped rendering of this report is the PDF.
        const rows = sortedEmployees.flatMap((employee) =>
            (employee?.sickDays ?? [])
                .filter((sickDay: any) => sickDay?.hours != null)
                .map((sickDay: any) => ({
                    [t('employee_name')]: employee?.employeeName ?? '',
                    [t('department')]: employee?.departmentName ?? '',
                    // Date-only ISO: excelExport turns this into a real Date so Excel offers the
                    // year > month tree instead of filtering it as text.
                    [t('month')]: sickDayMonthCell(sickDay),
                    // The API field is named `hours`, but the reports service divides minutes
                    // by 450 (a 7.5h workday), so the number is sick DAYS. This export is the
                    // first place a unit is put on it, so it must not say hours.
                    [t('sick_days')]: parseFloat(sickDay.hours)
                }))
        );

        downloadExcel(
            rows,
            t('sickness_report'),
            `Sickness_Report_${selectedDepartment}_${
                new Date().toISOString().split('T')[0]
            }.xlsx`
        );
    };

    const handleDownloadPdf = () => {
        downloadPdf.mutate(
            selectedDepartment === 'All' ? undefined : selectedDepartment,
            {
                onSuccess: ({ fileUrl }) => {
                    const anchor = document.createElement('a');
                    anchor.href = fileUrl;
                    anchor.click();
                    anchor.remove();
                }
            }
        );
    };

    const calculateGrandTotal = () => {
        return employeesList.reduce((total, employee) => {
            return total + parseFloat(employee.sum || 0);
        }, 0);
    };

    return (
        <Box>
            <SearchGrid container justifyContent={'space-between'}>
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                    <div>
                        <SMBold noWrap={true}>{t('filter_by')}:</SMBold>
                    </div>
                    <div style={{ width: '220px' }}>
                        <Dropdown
                            label={''}
                            choices={departments.map((d, _index) => {
                                return { label: d, value: d };
                            })}
                            value={selectedDepartment}
                            onChange={function (value: any | any[]): void {
                                setSelectedDepartment(value);
                            }}
                        ></Dropdown>
                    </div>
                </Stack>
                <Stack direction={'row'} gap={1}>
                    <SecondaryButtonRegular
                        onClick={handleDownloadExcel}
                        disabled={
                            getEmployeeSicknessQuery.isPending ||
                            sortedEmployees.length === 0
                        }
                        size="small"
                        sx={{ borderRadius: '5px' }}
                    >
                        {t('download_as_excel')}
                    </SecondaryButtonRegular>
                    <PrimaryButton
                        onClick={handleDownloadPdf}
                        loading={downloadPdf.isPending}
                        disabled={
                            getEmployeeSicknessQuery.isPending ||
                            sortedEmployees.length === 0
                        }
                        size="small"
                    >
                        {t('download_as_pdf')}
                    </PrimaryButton>
                </Stack>
            </SearchGrid>
            <TableCard>
                <Grid pt={2} pl={2} pr={2} container alignItems={'center'}>
                    <Grid
                        item
                        md={10}
                        flexDirection={'row'}
                        display={'flex'}
                        gap={1}
                        alignItems={'center'}
                    >
                        <Heading style={{ display: 'inline' }}>
                            Sickness Report
                        </Heading>
                        <Chip label={selectedDepartment} />
                    </Grid>
                </Grid>

                <Box mt={2}>
                    <TableContainer
                        style={
                            {
                                // height: 'calc(100vh - 40vh)'
                            }
                        }
                    >
                        <Table>
                            <TableHeader
                                style={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1
                                }}
                            >
                                <TableHeaderCell>
                                    <TableSortLabel
                                        direction={nameSortDirection}
                                        onClick={() => {
                                            setSumSortDirection(undefined);
                                            setNameSortDirection(
                                                nameSortDirection === 'asc'
                                                    ? 'desc'
                                                    : 'asc'
                                            );
                                        }}
                                    >
                                        {t('employee_name')}
                                    </TableSortLabel>
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    {t('department')}
                                </TableHeaderCell>

                                {employeesList?.[0]?.sickDays?.map(
                                    (sickDay: any) => {
                                        return (
                                            <TableHeaderCell
                                                align="right"
                                                key={sickDay?.monthName}
                                                style={{ minWidth: '75px' }}
                                            >
                                                {sickDay?.monthName}{' '}
                                                {dayjs(
                                                    sickDay?.year?.toString()
                                                ).format('YY')}
                                            </TableHeaderCell>
                                        );
                                    }
                                )}

                                <TableHeaderCell
                                    align="right"
                                    style={{
                                        minWidth: '70px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    <TableSortLabel
                                        direction={sumSortDirection}
                                        onClick={() => {
                                            setNameSortDirection(undefined);
                                            setSumSortDirection(
                                                sumSortDirection === 'asc'
                                                    ? 'desc'
                                                    : 'asc'
                                            );
                                        }}
                                    >
                                        {t('sum')}
                                    </TableSortLabel>
                                </TableHeaderCell>
                            </TableHeader>
                            <TableBody>
                                {sortedEmployees.map((employee) => {
                                    return (
                                        <TableRow key={employee.id}>
                                            <TableCell
                                            // style={{ textWrap: 'nowrap' }}
                                            >
                                                {employee?.employeeName}
                                            </TableCell>

                                            <TableCell>
                                                {employee?.departmentName}
                                            </TableCell>

                                            {employee?.sickDays?.map(
                                                (
                                                    sickDay: any,
                                                    index: number
                                                ) => {
                                                    return (
                                                        <TableCell
                                                            align="right"
                                                            key={index}
                                                        >
                                                            {sickDay?.hours
                                                                ? toDecimal(
                                                                      parseFloat(
                                                                          sickDay?.hours
                                                                      ),
                                                                      2
                                                                  )
                                                                : '-'}
                                                        </TableCell>
                                                    );
                                                }
                                            )}
                                            <TableCell
                                                align="right"
                                                style={{ fontWeight: 'bold' }}
                                            >
                                                {toDecimal(
                                                    parseFloat(employee?.sum),
                                                    2
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableFooterCell>
                                        <SMBold>{t('total')}</SMBold>
                                    </TableFooterCell>
                                    <TableFooterCell></TableFooterCell>
                                    {calculateMonthlyTotals().map(
                                        (total: number, index: number) => (
                                            <TableFooterCell
                                                align="right"
                                                key={index}
                                            >
                                                <SMBold>
                                                    {total > 0
                                                        ? toDecimal(total, 2)
                                                        : '-'}
                                                </SMBold>
                                            </TableFooterCell>
                                        )
                                    )}
                                    <TableFooterCell align="right">
                                        <SMBold>
                                            {toDecimal(
                                                calculateGrandTotal(),
                                                2
                                            )}
                                        </SMBold>
                                    </TableFooterCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </Box>
            </TableCard>
        </Box>
    );
};
